import { UNDERWRITING_STATUS_LABELS } from 'src/constants/oatfi-business/constants'
import {
  BusinessEntityType,
  RequestedLimitSource,
  UnderwritingStatus,
} from 'src/constants/oatfi-business/enums'
import type { OatfiBusiness, PlaidLink, SetupStatusChip } from 'src/interfaces/oatfiBusiness'

const CENTS_PER_DOLLAR = 100

const dollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

/** A nullable wire string: absent, null and '' all mean "not provided". */
function readOptionalString(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') {
    return null
  }

  return value
}

/**
 * Keeps a wire value only when it is a member of the enum. An unrecognised value resolves to
 * null rather than being cast: a status or entity type the backend adds later would otherwise
 * render under whichever label happened to match, misreporting a lender decision.
 */
function readEnumValue<T extends string>(value: unknown, members: T[]): T | null {
  if (typeof value !== 'string') {
    return null
  }

  const match = members.find((member) => {
    return member === value
  })

  return match ?? null
}

function readOptionalNumber(value: unknown): number | null {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null
  }

  return value
}

/**
 * Normalises an admin Oatfi business response into `OatfiBusiness`, or `null` when the body is
 * not a business object. Mirrors `parseOAuthClientResponse`.
 *
 * `id`, `partnerId` and `name` are the three fields the response DTO marks non-optional, so all
 * three are required for a body to count as a business. Everything else is nullable on the
 * entity and is normalised to null.
 */
export function parseOatfiBusinessResponse(raw: unknown): OatfiBusiness | null {
  if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }

  const response = raw as Record<string, unknown>

  const id = readOptionalString(response.id)
  const partnerId = readOptionalString(response.partnerId)
  const name = readOptionalString(response.name)

  if (id === null || partnerId === null || name === null) {
    return null
  }

  return {
    id,
    partnerId,
    name,
    underwritingStatus: readEnumValue(
      response.underwritingStatus,
      Object.values(UnderwritingStatus),
    ),
    dbaName: readOptionalString(response.dbaName),
    streetAddress: readOptionalString(response.streetAddress),
    city: readOptionalString(response.city),
    state: readOptionalString(response.state),
    postalCode: readOptionalString(response.postalCode),
    taxId: readOptionalString(response.taxId),
    contactFirstName: readOptionalString(response.contactFirstName),
    contactLastName: readOptionalString(response.contactLastName),
    contactEmail: readOptionalString(response.contactEmail),
    contactPhoneNumber: readOptionalString(response.contactPhoneNumber),
    businessStartDateMonth: readOptionalString(response.businessStartDateMonth),
    businessStartDateYear: readOptionalString(response.businessStartDateYear),
    businessEntityType: readEnumValue(
      response.businessEntityType,
      Object.values(BusinessEntityType),
    ),
    requestedLimitSource: readEnumValue(
      response.requestedLimitSource,
      Object.values(RequestedLimitSource),
    ),
    requestedCreditLimit: readOptionalNumber(response.requestedCreditLimit),
    plaidToken: readOptionalString(response.plaidToken),
    paymentMethodExternalId: readOptionalString(response.paymentMethodExternalId),
  }
}

/**
 * Normalises the Plaid link-token response. `hosted_link_url` is snake_case on the wire and
 * optional; when it is missing, `url` stays null and the card says so. It deliberately never
 * falls back to `linkToken`, which is an input to the Plaid JS SDK rather than a URL a customer
 * can open, and never synthesises a plaid.link address.
 */
export function parsePlaidLinkResponse(raw: unknown): PlaidLink | null {
  if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }

  const response = raw as Record<string, unknown>

  if (readOptionalString(response.linkToken) === null) {
    return null
  }

  return {
    url: readOptionalString(response.hosted_link_url),
    expiration: readOptionalString(response.expiration),
  }
}

/**
 * The four setup facts the strip renders, derived from the fetched business.
 *
 * These are independent facts, NOT a pipeline. Nothing in the backend orders them:
 * `requestUnderwriting` checks only that a business row exists, at both the controller and the
 * service layer, so an admin can request underwriting the moment the profile saves. Rendering
 * them as numbered steps would assert a required order that does not exist.
 *
 * "Documents" is deliberately absent. Supplemental uploads are a pass-through to Oatfi with no
 * entity, repository or read endpoint anywhere in para-node, so any progress shown for them
 * would be invented.
 */
export function deriveSetupStatus(business: OatfiBusiness | null): SetupStatusChip[] {
  const underwritingStatus = business?.underwritingStatus ?? null

  return [
    {
      key: 'profile',
      label: 'Business profile',
      value: business === null ? 'Not created' : 'Saved',
      isComplete: business !== null,
    },
    {
      key: 'bankLink',
      label: 'Bank link',
      value: business?.plaidToken ? 'Linked' : 'Not linked',
      isComplete: Boolean(business?.plaidToken),
    },
    {
      key: 'paymentMethod',
      label: 'Payment method',
      value: business?.paymentMethodExternalId ? 'Created' : 'Not created',
      isComplete: Boolean(business?.paymentMethodExternalId),
    },
    {
      key: 'underwriting',
      label: 'Underwriting',
      value:
        underwritingStatus === null
          ? 'Not requested'
          : UNDERWRITING_STATUS_LABELS[underwritingStatus],
      isComplete: underwritingStatus !== null,
    },
  ]
}

/**
 * Renders a cents figure as dollars for the requested-credit-limit hint. The unit is a frontend
 * decision the wire does not enforce (the field carries only @IsOptional()), so the form shows
 * the dollar equivalent live — an admin typing 50000 sees $500.00 and can catch the 100x slip
 * themselves.
 */
export function formatCentsAsDollars(cents: number | null | undefined): string | null {
  if (cents === null || cents === undefined || Number.isNaN(cents)) {
    return null
  }

  return dollarFormatter.format(cents / CENTS_PER_DOLLAR)
}
